/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Warehouse schema", description = "Details on the warehouse schema created by Hightouch Events", lastUpdatedDate = new Date(1695254400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Warehouse schema",
        description: "Details on the warehouse schema created by Hightouch Events",
        lastUpdatedDate: "Thu Sep 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Identifies",
          "depth": 0,
          "id": "identifies"
        }, {
          "value": "Tracks",
          "depth": 0,
          "id": "tracks"
        }, {
          "value": "Per-Event tables",
          "depth": 1,
          "id": "per-event-tables"
        }, {
          "value": "Pages",
          "depth": 0,
          "id": "pages"
        }, {
          "value": "Screens",
          "depth": 0,
          "id": "screens"
        }, {
          "value": "Groups",
          "depth": 0,
          "id": "groups"
        }, {
          "value": "How are the warehouse column types determined?",
          "depth": 1,
          "id": "how-are-the-warehouse-column-types-determined"
        }, {
          "value": "What happens if my event types change?",
          "depth": 1,
          "id": "what-happens-if-my-event-types-change"
        }, {
          "value": "How are complex objects handled?",
          "depth": 1,
          "id": "how-are-complex-objects-handled"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h1: "h1",
      h2: "h2",
      code: "code",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3",
      ul: "ul",
      li: "li",
      a: "a",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Hightouch automatically creates and manages tables for all event types. New\ntracked fields are automatically imported into the warehouse without additional\ndata engineering work."
      }), "\n", _jsx(_components.p, {
        children: "All the tables for an event source are written into the same schema in the\nwarehouse. By default, this schema name is autogenerated based on the event\nsource. However, the destination schema can be configured in the destination\nsettings."
      }), "\n", _jsx(_components.h1, {
        id: "tables",
        children: "Tables"
      }), "\n", _jsx(_components.h2, {
        id: "identifies",
        children: "Identifies"
      }), "\n", _jsxs(_components.p, {
        children: ["All ", _jsx(_components.code, {
          children: "identify"
        }), " events are written into the warehouse in the ", _jsx(_components.code, {
          children: "identifies"
        }), " table."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "id"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.code, {
                children: "messageId"
              }), " from the event"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "The anonymous id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "The user id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "<traits>"
              })
            }), _jsxs(_components.td, {
              children: ["The user traits provided in the ", _jsx(_components.code, {
                children: "identify"
              }), " request. Each key in the ", _jsx(_components.code, {
                children: "traits"
              }), " object is converted into a separate column."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context_<key>"
              })
            }), _jsx(_components.td, {
              children: "Additional context about the request. Often autocollected by the tracking SDKs."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "The clock-skew adjusted timestamp this event occurred. In most cases, this column should be used for timestamp analysis."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "received_at"
              })
            }), _jsxs(_components.td, {
              children: ["When Hightouch received the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sent_at"
              })
            }), _jsxs(_components.td, {
              children: ["When the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "original_timestamp"
              })
            }), _jsxs(_components.td, {
              children: ["The client-side timestamp for when the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tracks",
        children: "Tracks"
      }), "\n", _jsxs(_components.p, {
        children: ["All ", _jsx(_components.code, {
          children: "track"
        }), " events are written into the warehouse in the ", _jsx(_components.code, {
          children: "tracks"
        }), " table."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "id"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.code, {
                children: "messageId"
              }), " from the event"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "The anonymous id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "The user id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context_<key>"
              })
            }), _jsx(_components.td, {
              children: "Additional context about the request. Often autocollected by the tracking SDKs."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event_text"
              })
            }), _jsx(_components.td, {
              children: "The event name."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event"
              })
            }), _jsx(_components.td, {
              children: "The snake-cased version of the event name. This matches the table name autocreated by Hightouch."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "The clock-skew adjusted timestamp this event occurred. In most cases, this column should be used for timestamp analysis."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "received_at"
              })
            }), _jsxs(_components.td, {
              children: ["When Hightouch received the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sent_at"
              })
            }), _jsxs(_components.td, {
              children: ["When the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "original_timestamp"
              })
            }), _jsxs(_components.td, {
              children: ["The client-side timestamp for when the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "per-event-tables",
        children: "Per-Event tables"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch creates a separate table for each Track\nevent type. When these per-event tables are enabled, the event properties are\nwritten as separate columns within the table."
      }), "\n", _jsxs(_components.p, {
        children: ["When per-event tables are turned off, the events ", _jsx(_components.code, {
          children: "properties"
        }), " column is added\nto the ", _jsx(_components.code, {
          children: "tracks"
        }), " table so that custom properties are still accessible."]
      }), "\n", _jsx(_components.h2, {
        id: "pages",
        children: "Pages"
      }), "\n", _jsxs(_components.p, {
        children: ["All ", _jsx(_components.code, {
          children: "page"
        }), " events are written into the warehouse in the ", _jsx(_components.code, {
          children: "pages"
        }), " table."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "id"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.code, {
                children: "messageId"
              }), " from the event"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "The anonymous id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "The user id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context_<key>"
              })
            }), _jsx(_components.td, {
              children: "Additional context about the request. Often autocollected by the tracking SDKs."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "<properties>"
              })
            }), _jsxs(_components.td, {
              children: ["The page properties provided in the ", _jsx(_components.code, {
                children: "page"
              }), " request. Each key in the ", _jsx(_components.code, {
                children: "properties"
              }), " object is converted into a separate column."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "The clock-skew adjusted timestamp this event occurred. In most cases, this column should be used for timestamp analysis."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "received_at"
              })
            }), _jsxs(_components.td, {
              children: ["When Hightouch received the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sent_at"
              })
            }), _jsxs(_components.td, {
              children: ["When the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "original_timestamp"
              })
            }), _jsxs(_components.td, {
              children: ["The client-side timestamp for when the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "screens",
        children: "Screens"
      }), "\n", _jsxs(_components.p, {
        children: ["All ", _jsx(_components.code, {
          children: "screen"
        }), " events are written into the warehouse in the ", _jsx(_components.code, {
          children: "screens"
        }), " table."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "id"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.code, {
                children: "messageId"
              }), " from the event"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "The anonymous id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "The user id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context_<key>"
              })
            }), _jsx(_components.td, {
              children: "Additional context about the request. Often autocollected by the tracking SDKs."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "<properties>"
              })
            }), _jsxs(_components.td, {
              children: ["The screen properties provided in the ", _jsx(_components.code, {
                children: "screen"
              }), " request. Each key in the ", _jsx(_components.code, {
                children: "properties"
              }), " object is converted into a separate column."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "The clock-skew adjusted timestamp this event occurred. In most cases, this column should be used for timestamp analysis."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "received_at"
              })
            }), _jsxs(_components.td, {
              children: ["When Hightouch received the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sent_at"
              })
            }), _jsxs(_components.td, {
              children: ["When the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "original_timestamp"
              })
            }), _jsxs(_components.td, {
              children: ["The client-side timestamp for when the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "groups",
        children: "Groups"
      }), "\n", _jsxs(_components.p, {
        children: ["All ", _jsx(_components.code, {
          children: "group"
        }), " events are written into the warehouse in the ", _jsx(_components.code, {
          children: "groups"
        }), " table."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "id"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.code, {
                children: "messageId"
              }), " from the event"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "anonymous_id"
              })
            }), _jsx(_components.td, {
              children: "The anonymous id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "user_id"
              })
            }), _jsx(_components.td, {
              children: "The user id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "group_id"
              })
            }), _jsx(_components.td, {
              children: "The group id from the event"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "context_<key>"
              })
            }), _jsx(_components.td, {
              children: "Additional context about the request. Often autocollected by the tracking SDKs."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "<traits>"
              })
            }), _jsxs(_components.td, {
              children: ["The group traits provided in the ", _jsx(_components.code, {
                children: "group"
              }), " request. Each key in the ", _jsx(_components.code, {
                children: "traits"
              }), " object is converted into a separate column."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "The clock-skew adjusted timestamp this event occurred. In most cases, this column should be used for timestamp analysis."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "received_at"
              })
            }), _jsxs(_components.td, {
              children: ["When Hightouch received the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sent_at"
              })
            }), _jsxs(_components.td, {
              children: ["When the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "original_timestamp"
              })
            }), _jsxs(_components.td, {
              children: ["The client-side timestamp for when the tracking SDK sent the event. Used for calculating ", _jsx(_components.code, {
                children: "timestamp"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h1, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "how-are-the-warehouse-column-types-determined",
        children: "How are the warehouse column types determined?"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch determines what types to use for new columns based on the event\nvalues. Event values are categorized into one of the following types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "String"
        }), "\n", _jsx(_components.li, {
          children: "Integer"
        }), "\n", _jsx(_components.li, {
          children: "Float"
        }), "\n", _jsx(_components.li, {
          children: "Timestamp"
        }), "\n", _jsx(_components.li, {
          children: "Boolean"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Timestamps must be sent as ISO-8601 string."
      }), "\n", _jsx(_components.h3, {
        id: "what-happens-if-my-event-types-change",
        children: "What happens if my event types change?"
      }), "\n", _jsx(_components.p, {
        children: "When possible, Hightouch attempts to convert the values in the event payload\ninto the existing types in the warehouse. For example, if an event contains a\nInteger value for a field, but the current type in the warehouse is a String,\nthe Integer is automatically converted."
      }), "\n", _jsxs(_components.p, {
        children: ["If a conversion can't be safely done without losing data, Hightouch doesn't\nupload the event, and ", _jsx(_components.a, {
          href: "./error-handling",
          children: "retries it for 48 hours"
        }), ". This way, if\nyou fix the type in the warehouse, Hightouch will automatically sync the event."]
      }), "\n", _jsx(_components.h3, {
        id: "how-are-complex-objects-handled",
        children: "How are complex objects handled?"
      }), "\n", _jsx(_components.p, {
        children: "When tracking events with Objects as custom properties, Hightouch flattens\nnested columns into a flat schema."
      }), "\n", _jsx(_components.p, {
        children: "For example, the following properties:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"company\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"name\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Hightouch\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"location\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Would get imported into the warehouse as:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "company_name"
            }), _jsx(_components.th, {
              children: "company_location"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Hightouch"
            }), _jsx(_components.td, {
              children: "San Francisco"
            })]
          })
        })]
      })]
    });
  }
}
export default MDXContent;
